import React, { useEffect, useRef } from "react";
import { FaSync, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";

/*
 * VerschiebenJobIndicator — kleine Ecke-Anzeige für Hintergrund-Verschiebe-Jobs.
 *
 * Laufend:  Pille "Verschieben läuft… 3/7" + aktueller Dateiname.
 * Fertig:   dismissbares Ergebnis-Panel "5 verschoben, 1 Fehler" mit Liste
 *           der Fehler pro Datei (Move-Fehler, Trello-Fehler, Kompressions-
 *           Warnungen getrennt ausgewiesen).
 *
 * Maximal 3 Meldungen gleichzeitig sichtbar; erfolgreiche Fertig-Meldungen
 * schließen sich nach 30 Sekunden von selbst (Fehler bleiben bis zum Klick).
 *
 * Sitzt über der Ecke mit Ghost und Monitoring (bottom: 136px), damit alles sichtbar ist.
 */

const AUTO_DISMISS_MS = 30000;
const MAX_SICHTBAR = 3;

const S = {
  wrap: { position: "fixed", right: 20, bottom: 136, zIndex: 4000, display: "flex", flexDirection: "column", gap: 8, maxWidth: 460 },
  pill: {
    display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
    background: "var(--bg-card, #fff)", color: "var(--color-gray-800, #222)",
    border: "1px solid var(--color-info, #1976d2)", borderLeft: "5px solid var(--color-info, #1976d2)",
    borderRadius: 8, boxShadow: "0 6px 24px rgba(0,0,0,0.28)", fontSize: "0.85rem",
  },
  panel: (hatFehler) => ({
    background: "var(--bg-card, #fff)", color: "var(--color-gray-800, #222)",
    border: "1px solid " + (hatFehler ? "var(--color-error, #d32f2f)" : "var(--color-success, #2e7d32)"),
    borderLeft: "5px solid " + (hatFehler ? "var(--color-error, #d32f2f)" : "var(--color-success, #2e7d32)"),
    borderRadius: 8, padding: "12px 16px", boxShadow: "0 6px 24px rgba(0,0,0,0.28)", fontSize: "0.85rem",
  }),
  dismiss: {
    marginLeft: "auto", border: "none", background: "transparent", cursor: "pointer",
    fontSize: "1.1rem", color: "var(--color-gray-600, #666)", lineHeight: 1,
  },
  fehlerListe: {
    margin: "6px 0 0", paddingLeft: 18, fontSize: "0.78rem",
    color: "var(--color-error, #d32f2f)", maxHeight: 200, overflowY: "auto",
  },
  warnung: { color: "var(--color-warning, #ed6c02)" },
};

function fehlerZeilen(job) {
  const zeilen = [];
  (job.ergebnisse || []).forEach((e) => {
    if (e.fehler) zeilen.push({ text: `${e.dateiname}: ${e.fehler}`, warnung: false });
    if (e.ok && e.trello_fehler) zeilen.push({ text: `${e.dateiname}: verschoben, Trello-Karte fehlgeschlagen: ${e.trello_fehler}`, warnung: false });
    if (e.komprimierung_fehler) zeilen.push({ text: `${e.dateiname}: ${e.komprimierung_fehler}`, warnung: true });
  });
  return zeilen;
}

export default function VerschiebenJobIndicator({ jobs, onDismiss, vorlauf }) {
  const liste = Object.values(jobs || {});
  const timerRef = useRef({});

  // Erfolgreiche Fertig-Meldungen nach 30 s automatisch schließen,
  // damit der User sie nicht wegklicken muss. Fehler bleiben stehen.
  useEffect(() => {
    Object.values(jobs || {}).forEach((job) => {
      if (!job || job.status === "running") return;
      const hatFehler = job.status === "abgebrochen" || job.fehler_count > 0 || fehlerZeilen(job).length > 0;
      if (hatFehler || timerRef.current[job.job_id]) return;
      timerRef.current[job.job_id] = setTimeout(() => {
        delete timerRef.current[job.job_id];
        onDismiss(job.job_id);
      }, AUTO_DISMISS_MS);
    });
    // Timer aufräumen, deren Job inzwischen weg ist (z. B. manuell geschlossen)
    Object.keys(timerRef.current).forEach((id) => {
      if (!jobs || !jobs[id]) {
        clearTimeout(timerRef.current[id]);
        delete timerRef.current[id];
      }
    });
  }, [jobs, onDismiss]);

  useEffect(() => () => {
    Object.values(timerRef.current).forEach(clearTimeout);
  }, []);

  if (liste.length === 0 && !vorlauf) return null;

  // Maximal 3 Meldungen übereinander — die neuesten zuerst behalten
  const sichtbar = liste.slice(-MAX_SICHTBAR);
  const versteckt = liste.length - sichtbar.length;

  return (
    <div style={S.wrap}>
      <style>{"@keyframes vjispin{to{transform:rotate(360deg)}} .vji-spin{animation:vjispin 1s linear infinite}"}</style>
      {versteckt > 0 && (
        <div style={{ ...S.pill, borderColor: "var(--color-gray-400, #999)", borderLeftColor: "var(--color-gray-400, #999)", fontSize: "0.75rem", padding: "6px 12px" }}>
          + {versteckt} weitere Meldung{versteckt > 1 ? "en" : ""}
        </div>
      )}
      {vorlauf && (
        <div style={S.pill}>
          <FaSync className="vji-spin" style={{ color: "var(--color-info, #1976d2)", flexShrink: 0 }} />
          <div style={{ fontWeight: 600 }}>{vorlauf}</div>
        </div>
      )}
      {sichtbar.map((job) => {
        if (job.status === "running") {
          return (
            <div key={job.job_id} style={S.pill}>
              <FaSync className="vji-spin" style={{ color: "var(--color-info, #1976d2)", flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>
                  Verschieben läuft… {job.done}/{job.total || "?"}
                </div>
                {job.current_datei && (
                  <div style={{ fontSize: "0.72rem", color: "var(--color-gray-600, #666)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 340 }}>
                    {job.current_datei}
                  </div>
                )}
              </div>
            </div>
          );
        }

        const zeilen = fehlerZeilen(job);
        const abgebrochen = job.status === "abgebrochen";
        const hatFehler = abgebrochen || job.fehler_count > 0 || zeilen.length > 0;
        return (
          <div key={job.job_id} style={S.panel(hatFehler)}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {hatFehler
                ? <FaExclamationTriangle style={{ color: "var(--color-error, #d32f2f)", flexShrink: 0 }} />
                : <FaCheckCircle style={{ color: "var(--color-success, #2e7d32)", flexShrink: 0 }} />}
              <span style={{ fontWeight: 600 }}>
                {abgebrochen
                  ? "Verschieben unterbrochen — Liste neu laden"
                  : `${job.ok_count} verschoben${job.fehler_count ? `, ${job.fehler_count} Fehler` : ""}`}
              </span>
              <button style={S.dismiss} onClick={() => onDismiss(job.job_id)} title="Schließen">&#215;</button>
            </div>
            {zeilen.length > 0 && (
              <ul style={S.fehlerListe}>
                {zeilen.map((z, i) => (
                  <li key={i} style={z.warnung ? S.warnung : undefined}>{z.text}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
